/**
 * Content for all generic documentation pages.
 *
 * Each key is the URL slug. The two custom pages (quick-start, google-calendar)
 * use their own dedicated components and are NOT listed here.
 *
 * Section types supported by GenericDocPage:
 *   - intro:       { type: 'intro', body: 'paragraph text' }
 *   - steps:       { type: 'steps', title: 'How to X', items: [{ title, body }] }
 *   - list:        { type: 'list', title: 'Features', items: ['...', '...'] }
 *   - bullets:     { type: 'bullets', title: '...', items: [{ title, body }] }
 *   - tip:         { type: 'tip', tone: 'blue'|'amber'|'green', body: '...' }
 *   - troubleshoot:{ type: 'troubleshoot', items: [{ q, a }] }
 *   - formMock:    { type: 'formMock', url, title, subtitle, fields: [{label,value}] }
 *   - panelMock:   { type: 'panelMock', url, title, rows: [{label,value}] }
 */

export const docsContent = {
  /* ==================== Getting Started ==================== */
  'registration': {
    title: 'Creating Your Company Account',
    category: 'Getting Started',
    iconName: 'UserPlus',
    readTime: '3 min',
    description: 'Step-by-step guide to registering your moving company on MoveMate and getting your admin account set up.',
    sections: [
      { type: 'intro', body: 'Registering your company is a 2-minute process. Once done, you get an admin dashboard, 14-day free trial, and full access to every feature including mobile apps for your team.' },
      { type: 'steps', title: 'Sign up in 4 steps', items: [
        { title: 'Open the registration page', body: 'Go to admin.movemate.tech/register-company or click "Start Free Trial" from any page on movemate.tech.' },
        { title: 'Fill in your company details', body: 'Company name, your name, business email, and a strong password (8+ chars with a mix of letters, numbers, and a symbol).' },
        { title: 'Verify your email', body: 'We send a verification link within 60 seconds. Click it to activate your account. Check spam if it hasn\'t arrived in 2 minutes.' },
        { title: 'Log in and complete the setup wizard', body: 'First login launches a short wizard that guides you through company profile, pricing, and inviting your first team member.' },
      ]},
      { type: 'formMock', url: 'https://admin.movemate.tech/register-company', title: 'Register Your Company', subtitle: 'Start your 14-day free trial',
        fields: [
          { label: 'Company Name', value: 'Acme Moving Ltd' },
          { label: 'Business Email', value: 'hello@acmemoving.co.uk' },
          { label: 'Password', value: '••••••••' },
        ]
      },
      { type: 'tip', tone: 'blue', body: 'Use a business email (e.g. hello@yourcompany.co.uk). It shows up on customer-facing estimates and invoices.' },
      { type: 'troubleshoot', items: [
        { q: 'Email is already registered', a: 'Only one company account per email is allowed. Use a different email or recover your existing account via Forgot Password.' },
        { q: 'Password rejected as "too weak"', a: 'Add at least one uppercase letter, one number, and one symbol. Minimum 8 characters total.' },
      ]},
    ],
    related: ['quick-start', 'dashboard', 'first-job'],
  },

  'dashboard': {
    title: 'Dashboard Overview',
    category: 'Getting Started',
    iconName: 'LayoutDashboard',
    readTime: '4 min',
    description: 'Understand the MoveMate control panel — the left sidebar, quick stats, today\'s jobs, and notifications.',
    sections: [
      { type: 'intro', body: 'The dashboard is mission control for your moving company. From here you see every live job, today\'s schedule, team status, and revenue at a glance.' },
      { type: 'bullets', title: 'Key areas of the dashboard', items: [
        { title: 'Quick Stats (top strip)', body: 'Live counts of active jobs, pending estimates, unread messages, and today\'s revenue.' },
        { title: 'Today\'s Jobs (main panel)', body: 'Jobs scheduled for today with status chips — clickable to open the job detail view.' },
        { title: 'Left Sidebar', body: 'Primary navigation: Jobs, Customers, Team, Inventory, Estimates, Reports, Settings.' },
        { title: 'Notification Bell', body: 'Unread messages, job status changes, and system alerts. Click to see everything in one feed.' },
        { title: 'Company Switcher', body: 'If you manage multiple companies, switch between them from the top-right avatar menu.' },
      ]},
      { type: 'tip', tone: 'blue', body: 'Press Cmd/Ctrl+K anywhere to open the quick search — jump to any job, customer, or setting in one shortcut.' },
    ],
    related: ['quick-start', 'creating-jobs', 'company-settings'],
  },

  'first-job': {
    title: 'Adding Your First Job',
    category: 'Getting Started',
    iconName: 'Truck',
    readTime: '5 min',
    description: 'Create your first moving job in MoveMate with customer details, addresses, date, inventory, and team assignment.',
    sections: [
      { type: 'intro', body: 'A "job" is the central record for one customer move. It ties together the customer, both addresses, schedule, inventory, team, estimate, and invoice.' },
      { type: 'steps', title: 'Create a job', items: [
        { title: 'Click "+ New Job"', body: 'The blue button is in the top-right of the dashboard and every job list page.' },
        { title: 'Add customer details', body: 'Name, phone, email. If the customer exists in your CRM, start typing and pick them from the dropdown.' },
        { title: 'Set pickup & delivery addresses', body: 'Google Maps autocomplete suggests full addresses as you type. Distance and travel time are auto-calculated.' },
        { title: 'Pick the date and time', body: 'Choose the move date, start time, and an estimated number of hours.' },
        { title: 'Add inventory', body: 'Either manually add rooms/items, use an inventory template, or snap photos with the mobile app for AI scanning.' },
        { title: 'Assign your team', body: 'Pick a driver, one or more porters, and optionally a supervisor. Availability is shown in real time.' },
        { title: 'Generate and send estimate', body: 'Click "Save & Send Estimate" — the customer receives a branded PDF email with accept/reject buttons.' },
      ]},
      { type: 'tip', tone: 'green', body: 'Duplicate similar past jobs in one click: open any completed job and hit "Duplicate". All settings copy over except the date.' },
    ],
    related: ['creating-jobs', 'ai-scanning', 'estimates', 'team-assignments'],
  },

  /* ==================== Job Management ==================== */
  'creating-jobs': {
    title: 'Creating Jobs',
    category: 'Job Management',
    iconName: 'FilePlus',
    readTime: '6 min',
    description: 'Full guide to creating moving jobs in MoveMate — customer data, addresses, scheduling, inventory, teams, and estimates.',
    sections: [
      { type: 'intro', body: 'A complete job captures every detail of a move so your crew can execute without calling the office for info. This guide walks through every field on the new-job form.' },
      { type: 'bullets', title: 'Sections of the New Job form', items: [
        { title: 'Customer', body: 'Name, phone, email, and an optional company name for commercial moves. Existing customers auto-fill.' },
        { title: 'Addresses', body: 'From-address and to-address, with optional access notes (lifts, stairs, parking). Distance is auto-calculated.' },
        { title: 'Schedule', body: 'Move date, start time, estimated duration in hours. Multi-day moves are supported.' },
        { title: 'Service Type', body: 'Full move, packing only, storage in/out, delivery only — each type triggers a different estimate formula.' },
        { title: 'Inventory', body: 'Per-room item list with quantities, volumes (cubic feet), and fragility flags. Photo uploads supported.' },
        { title: 'Team & Vehicle', body: 'Assign staff and optionally a specific vehicle from your fleet.' },
        { title: 'Notes & Documents', body: 'Internal notes (team-visible only) and customer-facing notes on the estimate.' },
      ]},
      { type: 'tip', tone: 'amber', body: 'Required fields are marked with an asterisk. You can save a draft at any point and finish later — drafts appear in the "Draft Jobs" filter.' },
    ],
    related: ['first-job', 'job-workflow', 'estimates', 'team-assignments'],
  },

  'job-workflow': {
    title: 'Job Statuses & Workflow',
    category: 'Job Management',
    iconName: 'Workflow',
    readTime: '4 min',
    description: 'Understanding the MoveMate job lifecycle — from draft to scheduled, in progress, completed, and invoiced.',
    sections: [
      { type: 'intro', body: 'Every job moves through a defined workflow. Statuses trigger customer notifications, crew mobile updates, and billing actions automatically.' },
      { type: 'bullets', title: 'The 7 job statuses', items: [
        { title: 'Draft', body: 'Saved but not sent to the customer. Only visible internally.' },
        { title: 'Estimate Sent', body: 'Customer has received the quote email and can accept/reject from the portal.' },
        { title: 'Scheduled', body: 'Customer accepted. Team assigned, event synced to Google Calendar.' },
        { title: 'In Progress', body: 'Driver marked "Start Job" in the mobile app. Live GPS tracking is enabled.' },
        { title: 'Delivered', body: 'Items delivered and photographed. Awaiting customer signature.' },
        { title: 'Completed', body: 'Signature captured. Invoice generated automatically.' },
        { title: 'Cancelled', body: 'Job cancelled by customer or admin. Refunds (if any) handled manually.' },
      ]},
      { type: 'tip', tone: 'blue', body: 'Status changes trigger notification templates. Customize what customers receive under Settings → Notification Templates.' },
    ],
    related: ['creating-jobs', 'job-history', 'notifications'],
  },

  'team-assignments': {
    title: 'Assigning Teams to Jobs',
    category: 'Job Management',
    iconName: 'Users',
    readTime: '3 min',
    description: 'How to assign drivers, porters, and supervisors to jobs, see availability, and prevent double-booking.',
    sections: [
      { type: 'intro', body: 'MoveMate prevents double-booking by showing real-time availability when you assign crew to a job. Members already booked for overlapping hours are greyed out.' },
      { type: 'bullets', title: 'What you can assign', items: [
        { title: '1 Driver (required)', body: 'Responsible for navigation and GPS tracking. Must have "Driver" role.' },
        { title: '1+ Porters', body: 'Handle loading/unloading and QR scanning. Minimum 1, typically 2–4 depending on inventory volume.' },
        { title: 'Optional Supervisor', body: 'For larger jobs — oversees the crew and serves as the customer point of contact.' },
        { title: 'Optional Vehicle', body: 'Pick from your fleet. Vehicle size hints appear based on inventory volume.' },
      ]},
      { type: 'tip', tone: 'green', body: 'Bulk-assign with the "Auto-Assign" button — MoveMate picks the least-utilised available crew for the date, balancing workload across your team.' },
    ],
    related: ['creating-jobs', 'adding-users', 'driver-tracking'],
  },

  'job-history': {
    title: 'Job Timeline & History',
    category: 'Job Management',
    iconName: 'History',
    readTime: '3 min',
    description: 'Every change on a job is logged — see who did what and when, with a full audit trail.',
    sections: [
      { type: 'intro', body: 'Open any job and click "Timeline" to see a minute-by-minute history of every action: status changes, team assignments, customer messages, payment events, and more.' },
      { type: 'list', title: 'Events tracked in the timeline', items: [
        'Job created, edited, or cancelled',
        'Status transitions with timestamps',
        'Team assignment changes',
        'Customer accept/reject of estimate',
        'Photos and signatures uploaded',
        'Messages sent or received (chat & email)',
        'Payment captured, refunded, or failed',
      ]},
      { type: 'tip', tone: 'blue', body: 'Export a job timeline as a PDF for dispute resolution or insurance claims — it shows the complete chain of custody.' },
    ],
    related: ['job-workflow', 'customer-portal'],
  },

  /* ==================== Inventory & QR Codes ==================== */
  'ai-scanning': {
    title: 'AI Inventory Scanning',
    category: 'Inventory & QR Codes',
    iconName: 'Scan',
    readTime: '5 min',
    description: 'Use your phone camera to catalogue items in seconds — AI identifies, names, and estimates volume for every item.',
    sections: [
      { type: 'intro', body: 'AI Scanning turns a photo of a room into a fully-itemised inventory list. No more typing every box, sofa, or chair by hand.' },
      { type: 'steps', title: 'How to scan a room', items: [
        { title: 'Open a job in the mobile app', body: 'Go to the job, tap Inventory → "Scan with AI".' },
        { title: 'Take photos room-by-room', body: 'Aim for wide shots that show 3–6 items per photo. Good lighting dramatically improves accuracy.' },
        { title: 'Review and confirm', body: 'AI returns a list within ~5 seconds. Swipe to remove wrong items, tap to edit names or quantities.' },
        { title: 'Save to the job', body: 'Confirmed items get added to the job inventory with estimated volumes and fragility flags.' },
      ]},
      { type: 'tip', tone: 'green', body: 'AI scanning is available on the Premium and White Label plans. First 100 scans/month are included; beyond that £0.02 per scan.' },
      { type: 'troubleshoot', items: [
        { q: 'AI missed an item', a: 'Tap "+ Add Item" below the scan results to add it manually. The AI learns from your corrections over time.' },
        { q: 'Volume estimate seems wrong', a: 'Tap the item and edit the cubic-feet value directly. Your correction updates the inventory immediately.' },
      ]},
    ],
    related: ['qr-codes', 'inventory-templates', 'scan-console'],
  },

  'qr-codes': {
    title: 'QR Code System',
    category: 'Inventory & QR Codes',
    iconName: 'QrCode',
    readTime: '5 min',
    description: 'Print, assign, and track individual items across an entire move with unique QR code labels.',
    sections: [
      { type: 'intro', body: 'Every item in MoveMate can be assigned a QR code. Porters scan the code at loading and unloading to confirm the item is on the truck and delivered to the correct room.' },
      { type: 'bullets', title: 'QR workflow for one job', items: [
        { title: 'Generate labels', body: 'Open the job → Inventory → "Print Labels". Choose A4 sheet layout (24 labels) or roll-based label printers.' },
        { title: 'Attach to items', body: 'Stick the label on every box, piece of furniture, and loose item before loading.' },
        { title: 'Scan on load', body: 'Porters use the mobile app\'s scan console to check off each item as it\'s loaded onto the truck.' },
        { title: 'Scan on unload', body: 'At delivery, scanning again confirms the item arrived and lets the porter assign it to a destination room.' },
        { title: 'Customer visibility', body: 'Customers can see a live count of loaded/delivered items in their portal for peace of mind.' },
      ]},
      { type: 'tip', tone: 'amber', body: 'For high-value items (artwork, electronics), add a photo at the scan-point. This creates undisputable before/after evidence for insurance.' },
    ],
    related: ['ai-scanning', 'scan-console', 'customer-portal'],
  },

  'inventory-templates': {
    title: 'Inventory Templates',
    category: 'Inventory & QR Codes',
    iconName: 'ListChecks',
    readTime: '3 min',
    description: 'Save common inventory sets (1-bed flat, 3-bed house, office move) and reuse them across jobs.',
    sections: [
      { type: 'intro', body: 'Templates are pre-built item lists for common move types. Assign one to a job and instantly get a complete inventory baseline that you can tweak.' },
      { type: 'list', title: 'Popular starter templates', items: [
        '1-Bed Flat (~40 items, ~250 cu ft)',
        '2-Bed Flat (~70 items, ~400 cu ft)',
        '3-Bed House (~110 items, ~650 cu ft)',
        '4-Bed House (~150 items, ~900 cu ft)',
        'Small Office (~35 items, ~200 cu ft)',
        'Storage-Only Move (empty — you fill it in)',
      ]},
      { type: 'steps', title: 'Create your own template', items: [
        { title: 'Go to Settings → Inventory Templates', body: 'Click "+ New Template".' },
        { title: 'Name it and add rooms/items', body: 'Add every item you expect to see. Volumes auto-calculate.' },
        { title: 'Save and use', body: 'On the next New Job, pick the template from the "Load Template" dropdown.' },
      ]},
    ],
    related: ['ai-scanning', 'qr-codes', 'creating-jobs'],
  },

  'scan-console': {
    title: 'Scan Console',
    category: 'Inventory & QR Codes',
    iconName: 'MonitorCheck',
    readTime: '4 min',
    description: 'The loading/unloading verification dashboard — track items in real time as they\'re scanned onto and off the truck.',
    sections: [
      { type: 'intro', body: 'The Scan Console is a live view that fills up as your porters scan items. Office staff can see progress remotely; customers see a simplified version in their portal.' },
      { type: 'bullets', title: 'What you see in the console', items: [
        { title: 'Progress bar', body: '"142 of 185 items loaded" — updates in real time.' },
        { title: 'Room-by-room breakdown', body: 'Which rooms are fully scanned, which still have pending items.' },
        { title: 'Unscanned items', body: 'Any item not yet scanned shows in red so nothing is forgotten.' },
        { title: 'Fragile/high-value flags', body: 'Fragile items are highlighted to remind porters to take extra photos.' },
        { title: 'Timestamps', body: 'Scan time for every item — helpful for disputes ("this was loaded at 11:42").' },
      ]},
      { type: 'tip', tone: 'blue', body: 'If an item wasn\'t assigned a QR label (last-minute addition), tap "Add Walk-On Item" in the mobile app and take a photo. It joins the scan console instantly.' },
    ],
    related: ['qr-codes', 'ai-scanning', 'driver-app'],
  },

  /* ==================== Estimates & Billing ==================== */
  'estimates': {
    title: 'Creating Estimates',
    category: 'Estimates & Billing',
    iconName: 'FileText',
    readTime: '5 min',
    description: 'Generate professional, branded quotes for your customers with itemised pricing and auto-calculated totals.',
    sections: [
      { type: 'intro', body: 'Every job produces an estimate — a PDF quote that the customer receives by email with accept/reject buttons. Totals compute automatically from your pricing settings and the job\'s details.' },
      { type: 'bullets', title: 'What\'s in an estimate', items: [
        { title: 'Header', body: 'Your logo, company details, quote number, and customer name/address.' },
        { title: 'Service summary', body: 'Move date, route, and estimated duration.' },
        { title: 'Itemised costs', body: 'Labour, distance, materials, packing services, storage — each line shown separately.' },
        { title: 'Totals', body: 'Subtotal, VAT (at your configured rate), and grand total in your currency.' },
        { title: 'Terms & deposit', body: 'Your terms of service plus deposit amount and validity period (default: 14 days).' },
        { title: 'Accept/Reject buttons', body: 'Customers can accept or reject the quote directly from the email — no login required.' },
      ]},
      { type: 'tip', tone: 'green', body: 'Customer accepts → status auto-flips to Scheduled → Google Calendar event created → team gets push notifications. Zero manual work.' },
    ],
    related: ['pricing-setup', 'distance-pricing', 'payments'],
  },

  'pricing-setup': {
    title: 'Pricing Configuration',
    category: 'Estimates & Billing',
    iconName: 'Calculator',
    readTime: '6 min',
    description: 'Set up hourly labour rates, per-mile travel costs, VAT, deposits, and everything else that drives estimate totals.',
    sections: [
      { type: 'intro', body: 'Pricing lives in Settings → Estimate Settings. Every change applies to new estimates going forward (existing estimates are not re-priced).' },
      { type: 'bullets', title: 'The 5 pricing tabs', items: [
        { title: 'General', body: 'VAT rate, deposit %, quote validity days, currency.' },
        { title: 'Distance', body: 'Per-mile or per-km rate, free miles included, return-leg charging.' },
        { title: 'Labour', body: 'Hourly rates per role (driver, porter, supervisor), minimum hours, overtime multiplier.' },
        { title: 'Materials', body: 'Prices for boxes (small/medium/large), bubble wrap, tape, and packing paper.' },
        { title: 'Packing Services', body: 'Per-room, per-hour, or per-volume packing charges.' },
      ]},
      { type: 'tip', tone: 'amber', body: 'Always review your pricing after changing VAT rates — some estimates may need manual re-quoting if sent before the change.' },
    ],
    related: ['estimate-settings', 'distance-pricing', 'storage-pricing'],
  },

  'distance-pricing': {
    title: 'Distance-Based Pricing',
    category: 'Estimates & Billing',
    iconName: 'MapPin',
    readTime: '3 min',
    description: 'Auto-calculate travel costs using Google Maps distance between pickup and delivery addresses.',
    sections: [
      { type: 'intro', body: 'When you enter both addresses on a job, MoveMate fetches the driving distance and multiplies it by your configured per-mile rate to produce a travel charge line on the estimate.' },
      { type: 'bullets', title: 'Configuration options', items: [
        { title: 'Rate', body: 'Set in £/mile or £/km under Settings → Estimate Settings → Distance.' },
        { title: 'Free miles', body: 'Optional allowance — e.g. first 10 miles free, then £1.50/mile after.' },
        { title: 'Return-leg charging', body: 'Toggle whether you bill for the empty-truck return to depot.' },
        { title: 'Minimum travel charge', body: 'Ensures very short trips still cover fuel and driver time.' },
      ]},
      { type: 'tip', tone: 'blue', body: 'Distance is calculated once at estimate time. If the address changes later, click "Recalculate" on the estimate to refresh.' },
    ],
    related: ['pricing-setup', 'google-maps'],
  },

  'storage-pricing': {
    title: 'Storage Pricing',
    category: 'Estimates & Billing',
    iconName: 'Package',
    readTime: '3 min',
    description: 'Configure monthly storage rates — per-crate, per-cubic-foot, or flat-rate options.',
    sections: [
      { type: 'intro', body: 'Storage is billed monthly in MoveMate. Configure your preferred model once and it applies to every storage unit your customers rent.' },
      { type: 'bullets', title: 'Three billing models', items: [
        { title: 'Per-crate', body: 'Fixed £X per standard crate per month. Simple and predictable.' },
        { title: 'Per-cubic-foot', body: 'Billed by total volume stored. Best for mixed large and small items.' },
        { title: 'Flat-rate per unit', body: 'A set monthly fee per storage unit, regardless of contents.' },
      ]},
      { type: 'tip', tone: 'green', body: 'Add a handling charge (loading in / loading out) separately under Storage Handling Charges — covers the labour of moving items in and out of storage.' },
    ],
    related: ['storage-units', 'storage-models', 'storage-handling'],
  },

  'payments': {
    title: 'Payment Methods',
    category: 'Estimates & Billing',
    iconName: 'CreditCard',
    readTime: '4 min',
    description: 'Accept card payments, bank transfers, and cash — with Stripe handling the card processing.',
    sections: [
      { type: 'intro', body: 'MoveMate integrates with Stripe to accept card payments directly from the customer portal. You can also record bank transfers and cash manually.' },
      { type: 'bullets', title: 'Supported payment methods', items: [
        { title: 'Card (via Stripe)', body: 'Visa, Mastercard, Amex, Apple Pay, Google Pay. Customer pays from the portal — you see the status in real time.' },
        { title: 'Bank transfer', body: 'Display your bank details on the invoice. When funds arrive, mark the invoice as paid manually.' },
        { title: 'Cash on delivery', body: 'Your driver collects cash and marks the job as paid via the mobile app.' },
      ]},
      { type: 'steps', title: 'Connecting Stripe', items: [
        { title: 'Go to Settings → Integrations → Stripe', body: 'Click "Connect Stripe Account".' },
        { title: 'Sign in to Stripe', body: 'Log in with your Stripe account (or create one — takes 5 minutes for UK businesses).' },
        { title: 'Authorise MoveMate', body: 'Stripe asks you to authorise charges on your behalf. Click Allow.' },
        { title: 'Done', body: 'Payment buttons now appear on customer-facing estimates and invoices.' },
      ]},
    ],
    related: ['stripe', 'estimates', 'customer-portal'],
  },

  /* ==================== Team Management ==================== */
  'adding-users': {
    title: 'Adding Team Members',
    category: 'Team Management',
    iconName: 'UserPlus',
    readTime: '3 min',
    description: 'Invite drivers, porters, sales reps, and other admins to your MoveMate account.',
    sections: [
      { type: 'intro', body: 'Go to Team → User Management and click "+ Invite User". Enter their name, email, and role — they get an invitation email with a setup link.' },
      { type: 'bullets', title: 'Roles you can assign', items: [
        { title: 'Admin', body: 'Full access — use sparingly (only your co-founders/managers).' },
        { title: 'Driver', body: 'Access to the mobile app for assigned jobs, GPS tracking on.' },
        { title: 'Porter', body: 'Mobile app access for scanning items on assigned jobs.' },
        { title: 'Supervisor', body: 'Crew lead — can mark jobs as complete and capture signatures.' },
        { title: 'Sales Rep', body: 'Creates estimates and talks to customers. Cannot access financial settings.' },
      ]},
      { type: 'tip', tone: 'amber', body: 'Invitations expire after 7 days. Go to pending invites and click "Resend" to issue a fresh link.' },
    ],
    related: ['roles', 'driver-tracking', 'time-tracking'],
  },

  'roles': {
    title: 'Roles & Permissions',
    category: 'Team Management',
    iconName: 'Shield',
    readTime: '4 min',
    description: 'Full breakdown of what each role can see and do in MoveMate.',
    sections: [
      { type: 'intro', body: 'Roles are fixed permission sets. We don\'t support custom permissions today — but 95% of moving companies fit into the five built-in roles.' },
      { type: 'bullets', title: 'Permission matrix', items: [
        { title: 'Admin', body: 'Everything: create/edit/delete jobs, manage team, change pricing, view reports, access billing.' },
        { title: 'Sales Rep', body: 'Create and edit jobs, generate estimates, message customers. Cannot view other reps\' customers, settings, or reports.' },
        { title: 'Supervisor', body: 'View all assigned jobs, complete jobs, capture signatures, message the team. Cannot edit pricing or billing.' },
        { title: 'Driver', body: 'View jobs assigned to them, update status, navigate, scan items. Mobile app only.' },
        { title: 'Porter', body: 'View jobs assigned to them, scan items, take photos. Mobile app only.' },
      ]},
      { type: 'tip', tone: 'blue', body: 'A single person can only have ONE role. If you need someone who sells AND drives, give them the higher role (Sales Rep) — they can still be assigned to jobs as the driver.' },
    ],
    related: ['adding-users', 'driver-tracking'],
  },

  'driver-tracking': {
    title: 'Driver Tracking',
    category: 'Team Management',
    iconName: 'MapPin',
    readTime: '4 min',
    description: 'Real-time GPS location of your drivers during jobs — for ETAs, dispatch, and customer updates.',
    sections: [
      { type: 'intro', body: 'When a driver taps "Start Job" in the mobile app, GPS tracking begins. Their location updates every 30 seconds on your dispatch map and in the customer portal.' },
      { type: 'bullets', title: 'What tracking enables', items: [
        { title: 'Live dispatch map', body: 'See every active driver on a single map with job colour-coding.' },
        { title: 'Customer ETAs', body: 'Portal shows "Driver will arrive in ~12 minutes" with a live route.' },
        { title: 'Automatic milestones', body: 'Arrival and departure from each address are timestamped automatically.' },
        { title: 'Route replay', body: 'After the job, review the full driven route with speed and stops.' },
      ]},
      { type: 'tip', tone: 'amber', body: 'Tracking stops automatically when the driver taps "Complete Job" — drivers are NOT tracked outside of jobs. This is important for privacy and battery life.' },
    ],
    related: ['driver-app', 'google-maps', 'time-tracking'],
  },

  'time-tracking': {
    title: 'Time Tracking',
    category: 'Team Management',
    iconName: 'Clock',
    readTime: '3 min',
    description: 'Track work hours per job per team member — for payroll, overtime, and customer billing accuracy.',
    sections: [
      { type: 'intro', body: 'When a crew member taps "Start Job", MoveMate starts their timer. "Complete Job" stops it. You get an accurate hours report per person per week.' },
      { type: 'list', title: 'What\'s tracked', items: [
        'Start time, end time, total hours per job',
        'Break times (if drivers log them)',
        'Overtime hours (beyond your configured daily limit)',
        'Travel time (between jobs in the same day)',
        'Weekly totals per team member',
      ]},
      { type: 'tip', tone: 'green', body: 'Export weekly hours to CSV for your payroll system — Reports → Time Tracking → Export.' },
    ],
    related: ['driver-tracking', 'driver-app'],
  },

  /* ==================== Customer Communication ==================== */
  'chat': {
    title: 'Chat System',
    category: 'Customer Communication',
    iconName: 'MessageCircle',
    readTime: '3 min',
    description: 'Real-time messaging between office, crew, and customers — all logged against the relevant job.',
    sections: [
      { type: 'intro', body: 'Every job has its own chat thread. Messages between office, crew, and customers are logged forever — useful for disputes and audits.' },
      { type: 'bullets', title: 'Who can chat where', items: [
        { title: 'Customer ↔ Office', body: 'Customer messages via the portal; office replies from the job detail page. Also delivered as email if customer is offline.' },
        { title: 'Office ↔ Crew', body: 'Private internal thread not visible to customers. Used for last-minute dispatch changes.' },
        { title: 'Crew ↔ Crew', body: 'Team members on the same job can message each other during execution.' },
      ]},
      { type: 'tip', tone: 'blue', body: 'Pin important messages at the top of a chat thread — useful for access codes, parking notes, and special instructions.' },
    ],
    related: ['notifications', 'customer-portal'],
  },

  'notifications': {
    title: 'Notification Templates',
    category: 'Customer Communication',
    iconName: 'Bell',
    readTime: '5 min',
    description: 'Customize every automatic email and SMS your customers receive — quote sent, scheduled, reminders, delivered, and more.',
    sections: [
      { type: 'intro', body: 'Settings → Notification Templates shows every automatic message MoveMate sends. Edit the subject, body, and delivery channel for each event — all with your branding.' },
      { type: 'bullets', title: 'Events with templates', items: [
        { title: 'estimate.sent', body: 'When a customer receives their quote.' },
        { title: 'job.scheduled', body: 'Customer accepted — confirms move date and crew.' },
        { title: 'job.reminder', body: 'Sent 24 hours and 2 hours before the move.' },
        { title: 'job.in_progress', body: 'Driver started — customer gets a tracking link.' },
        { title: 'job.completed', body: 'Delivery done — invoice and review request attached.' },
        { title: 'invoice.sent', body: 'Invoice generated and emailed.' },
        { title: 'invoice.paid', body: 'Payment receipt (thank you message).' },
      ]},
      { type: 'tip', tone: 'green', body: 'Use the "Preview as Email" button to see exactly what the customer receives, rendered with real data from a sample job.' },
    ],
    related: ['notification-settings', 'chat'],
  },

  'customer-portal': {
    title: 'Customer Portal',
    category: 'Customer Communication',
    iconName: 'Globe',
    readTime: '4 min',
    description: 'A branded, mobile-friendly web portal where your customers can track their move, pay, and communicate — all without logging in.',
    sections: [
      { type: 'intro', body: 'Every customer gets a unique, secure portal link in their estimate email. From the portal they can accept quotes, track driver location, chat with your office, and pay invoices — no account required.' },
      { type: 'list', title: 'What customers can do', items: [
        'Review and accept/reject quotes with 1 click',
        'View job schedule and team assigned to their move',
        'Track driver GPS location on move day',
        'Chat with the office in real time',
        'View and pay invoices via Stripe',
        'Download receipts and estimate PDFs',
        'Upload floor-plan photos or access codes',
      ]},
      { type: 'tip', tone: 'blue', body: 'White Label plan users can customize the portal colours, logo, and domain — so it looks like your own site.' },
    ],
    related: ['chat', 'payments', 'white-label'],
  },

  /* ==================== Storage Management ==================== */
  'storage-units': {
    title: 'Storage Units',
    category: 'Storage Management',
    iconName: 'Building2',
    readTime: '3 min',
    description: 'Create and manage storage units — each one is a physical container (crate, pallet, room) you rent to customers.',
    sections: [
      { type: 'intro', body: 'Storage units are the physical containers your customers rent — individual crates, pallets, or rooms at your facility. Each unit has a location, size, and customer assignment.' },
      { type: 'bullets', title: 'What to configure per unit', items: [
        { title: 'Unit ID', body: 'A unique code (e.g. "A-12", "Pallet-47") — printed on a QR label attached to the crate.' },
        { title: 'Location', body: 'Which facility, row, and shelf it sits on — critical for large warehouses.' },
        { title: 'Size', body: 'Dimensions or cubic-foot capacity.' },
        { title: 'Status', body: 'Available, Reserved, Occupied, or Quarantined (awaiting cleaning).' },
        { title: 'Customer', body: 'Current renter (auto-assigned when a storage-in job completes).' },
      ]},
      { type: 'tip', tone: 'amber', body: 'Print permanent QR labels for each unit. Scanning a unit in the warehouse instantly shows you which customer\'s items are inside.' },
    ],
    related: ['storage-models', 'storage-handling', 'storage-pricing'],
  },

  'storage-models': {
    title: 'Storage Pricing Models',
    category: 'Storage Management',
    iconName: 'Calculator',
    readTime: '3 min',
    description: 'Decide how you charge for storage — by the box, by cubic-foot, or flat-rate per unit.',
    sections: [
      { type: 'intro', body: 'Choose once, apply to all. The pricing model is set at the company level and applies to every storage unit and rental.' },
      { type: 'bullets', title: 'Which model to pick', items: [
        { title: 'Per-box (£X / crate / month)', body: 'Best for simple warehouses with standard-sized crates. Predictable for customers.' },
        { title: 'Per-cubic-foot (£X / cu ft / month)', body: 'Fairest when customers store mixed item sizes. Requires accurate volume capture on load-in.' },
        { title: 'Flat-rate per unit (£X / unit / month)', body: 'Easiest billing — one line, one price. Best for companies with uniform storage rooms.' },
      ]},
      { type: 'tip', tone: 'blue', body: 'You can switch models at any time in Settings → Storage → Pricing Model. Existing rentals keep their original pricing; new ones use the updated model.' },
    ],
    related: ['storage-units', 'storage-pricing', 'storage-handling'],
  },

  'storage-handling': {
    title: 'Storage Handling Charges',
    category: 'Storage Management',
    iconName: 'Truck',
    readTime: '3 min',
    description: 'Configure the labour fees for loading items into storage and retrieving them later.',
    sections: [
      { type: 'intro', body: 'Monthly storage fees cover the unit itself. Handling charges cover the labour of moving items in and out — configured separately so the customer sees transparent line-items.' },
      { type: 'bullets', title: 'Charge types', items: [
        { title: 'Load-In Fee', body: 'Flat fee or hourly rate when items are first moved into storage.' },
        { title: 'Load-Out Fee', body: 'Charged when the customer retrieves items — either full retrieval or partial.' },
        { title: 'Partial Retrieval Surcharge', body: 'Optional extra for picking specific items out mid-rental (more labour than a full retrieval).' },
        { title: 'Minimum Visit Charge', body: 'Ensures even small partial retrievals cover your fixed costs.' },
      ]},
      { type: 'tip', tone: 'green', body: 'Use MoveMate\'s "Storage-In" and "Storage-Out" service types on the job — handling charges apply automatically.' },
    ],
    related: ['storage-pricing', 'storage-models'],
  },

  /* ==================== Integrations ==================== */
  'google-maps': {
    title: 'Google Maps',
    category: 'Integrations',
    iconName: 'MapPin',
    readTime: '3 min',
    description: 'Address autocomplete, distance calculation, and driver route planning — all powered by Google Maps.',
    sections: [
      { type: 'intro', body: 'MoveMate uses Google Maps Platform for every address-related feature. You need your own API key to activate it for your company.' },
      { type: 'steps', title: 'Getting a Google Maps API key', items: [
        { title: 'Go to console.cloud.google.com', body: 'Create a project (or pick an existing one).' },
        { title: 'Enable 3 APIs', body: 'Maps JavaScript API, Places API, and Distance Matrix API.' },
        { title: 'Create an API key', body: 'APIs & Services → Credentials → "+ Create Credentials" → API key. Restrict it to your domains for security.' },
        { title: 'Paste the key in MoveMate', body: 'Settings → Integrations → Google Maps → paste key → Save.' },
      ]},
      { type: 'tip', tone: 'amber', body: 'Google Maps Platform has a generous free tier ($200/month credit). A typical moving company uses £3-£8/month of API calls.' },
    ],
    related: ['driver-tracking', 'google-calendar'],
  },

  'stripe': {
    title: 'Stripe Integration',
    category: 'Integrations',
    iconName: 'CreditCard',
    readTime: '4 min',
    description: 'Accept card payments directly from your customers via Stripe.',
    sections: [
      { type: 'intro', body: 'Stripe is the global gold standard for accepting online payments. MoveMate integrates with Stripe so your customers can pay estimates and invoices by card directly from the portal.' },
      { type: 'steps', title: 'Connecting Stripe', items: [
        { title: 'Create a Stripe account', body: 'Go to stripe.com and sign up. UK business verification typically takes 1-2 business days.' },
        { title: 'Go to Settings → Integrations → Stripe', body: 'Click "Connect Stripe Account".' },
        { title: 'Complete Stripe Connect flow', body: 'Stripe walks you through authorisation — it takes ~2 minutes.' },
        { title: 'Test with a small transaction', body: 'Create a £1 test invoice and pay it from your own card to verify end-to-end flow.' },
      ]},
      { type: 'tip', tone: 'blue', body: 'Stripe fees are 1.4% + 20p for UK cards. MoveMate adds no markup — what Stripe takes is exactly what it takes.' },
    ],
    related: ['payments', 'estimates'],
  },

  /* ==================== Mobile Apps ==================== */
  'mobile-overview': {
    title: 'Mobile App Overview',
    category: 'Mobile Apps',
    iconName: 'Smartphone',
    readTime: '3 min',
    description: 'We have two mobile apps — one for your crew (drivers, porters) and one for your customers.',
    sections: [
      { type: 'intro', body: 'MoveMate ships two distinct mobile apps. The Crew app is for drivers and porters to execute jobs on the ground. The Customer app is for your customers to track moves, message you, and pay.' },
      { type: 'bullets', title: 'Crew App (iOS & Android)', items: [
        { title: 'Assigned jobs', body: 'Full details of today\'s jobs — addresses, items, customer contact.' },
        { title: 'Navigation', body: 'One-tap directions via Google Maps.' },
        { title: 'QR scanning', body: 'Camera-based item scanning at load and unload.' },
        { title: 'Photo capture', body: 'Condition photos and signatures stored against the job.' },
        { title: 'Offline support', body: 'Works without signal; syncs when reconnected.' },
      ]},
      { type: 'bullets', title: 'Customer App (iOS & Android)', items: [
        { title: 'Booking', body: 'Request a quote directly from the app.' },
        { title: 'Live tracking', body: 'See driver location during move day.' },
        { title: 'Chat', body: 'Message your moving company in real time.' },
        { title: 'Payment', body: 'Pay invoices via Apple Pay, Google Pay, or card.' },
      ]},
    ],
    related: ['driver-app', 'customer-app', 'apk-install'],
  },

  'driver-app': {
    title: 'Driver App Features',
    category: 'Mobile Apps',
    iconName: 'Truck',
    readTime: '4 min',
    description: 'Everything your drivers and porters can do from the MoveMate crew mobile app.',
    sections: [
      { type: 'intro', body: 'The crew app is a porter/driver\'s mission-control. Everything needed to execute a job is one tap away.' },
      { type: 'list', title: 'Features for drivers & porters', items: [
        'View today\'s job list with times and status',
        'One-tap navigation to pickup and delivery addresses',
        'Mark statuses: arrived, started, in transit, delivered',
        'Scan QR codes on items to verify loading',
        'Capture photos of items, damages, access points',
        'Capture customer e-signature on delivery',
        'Chat with office and supervisor',
        'Clock in / clock out for payroll',
        'Works offline — syncs when back online',
      ]},
      { type: 'tip', tone: 'amber', body: 'Drivers only see jobs they\'re assigned to — privacy-first design. They can\'t browse other customers or jobs.' },
    ],
    related: ['mobile-overview', 'driver-tracking', 'scan-console'],
  },

  'customer-app': {
    title: 'Customer App Features',
    category: 'Mobile Apps',
    iconName: 'Smartphone',
    readTime: '3 min',
    description: 'What your customers can do from the MoveMate mobile app on their phone.',
    sections: [
      { type: 'intro', body: 'The customer app is an optional convenience — everything it does is also available via the web portal. Some customers prefer an app icon on their home screen with push notifications.' },
      { type: 'list', title: 'Features for customers', items: [
        'Request a free moving quote',
        'Upload room photos for faster quoting',
        'Review and accept/reject estimates',
        'See a live map of their driver on move day',
        'Chat with the moving company',
        'Receive push notifications for status changes',
        'Pay invoices with Apple Pay / Google Pay',
        'View and download receipts',
      ]},
      { type: 'tip', tone: 'blue', body: 'The customer app is branded for White Label customers — your logo, your colours, your name in the app stores.' },
    ],
    related: ['mobile-overview', 'customer-portal', 'white-label'],
  },

  'apk-install': {
    title: 'Installing the APK',
    category: 'Mobile Apps',
    iconName: 'Download',
    readTime: '2 min',
    description: 'How to install the Android APK directly (when you don\'t want to use the Play Store).',
    sections: [
      { type: 'intro', body: 'For beta testers and White Label customers with custom builds, MoveMate provides a direct APK download. This guide is for Android only — iOS apps must come from TestFlight or the App Store.' },
      { type: 'steps', title: 'Installing an APK on Android', items: [
        { title: 'Enable "Unknown Sources"', body: 'Settings → Apps & Notifications → Special app access → Install unknown apps → allow your browser.' },
        { title: 'Download the APK', body: 'We email you a secure download link — tap it in your Android browser.' },
        { title: 'Tap the downloaded file', body: 'Android prompts to confirm installation. Tap "Install".' },
        { title: 'Open and log in', body: 'App icon appears on your home screen. Log in with your MoveMate credentials.' },
      ]},
      { type: 'tip', tone: 'amber', body: 'Disable "Unknown Sources" afterwards for security. MoveMate APK installations don\'t need it enabled after the first install.' },
    ],
    related: ['mobile-overview', 'driver-app'],
  },

  /* ==================== Settings & Configuration ==================== */
  'company-settings': {
    title: 'Company Settings',
    category: 'Settings & Configuration',
    iconName: 'Building',
    readTime: '4 min',
    description: 'Your company profile — logo, contact details, hours, service area, and currency.',
    sections: [
      { type: 'intro', body: 'Settings → Company Settings is where your brand lives. Everything here shows up on estimates, invoices, customer emails, and the customer portal.' },
      { type: 'bullets', title: 'Key fields', items: [
        { title: 'Logo', body: 'PNG or SVG, at least 512×512 px. Displays on every customer-facing document.' },
        { title: 'Company Name', body: 'Your legal or trading name — appears as the sender on emails.' },
        { title: 'Address', body: 'Used on invoices and returned-to-sender correspondence.' },
        { title: 'Phone & Email', body: 'Your primary customer contacts — surfaced in the portal.' },
        { title: 'Business Hours', body: 'Customer chat shows these hours so they know when to expect replies.' },
        { title: 'Currency & VAT Number', body: 'Drives estimate totals and tax-compliant invoicing.' },
      ]},
      { type: 'tip', tone: 'green', body: 'Changes apply immediately. Existing estimates/invoices keep their original branding; new documents use the latest settings.' },
    ],
    related: ['white-label', 'estimate-settings', 'quick-start'],
  },

  'white-label': {
    title: 'White-Label Branding',
    category: 'Settings & Configuration',
    iconName: 'Palette',
    readTime: '5 min',
    description: 'Make MoveMate completely yours — custom domain, logo, colours, email sender, and mobile apps.',
    sections: [
      { type: 'intro', body: 'White Label customers get a MoveMate experience that bears no MoveMate branding. Your company name is what customers see everywhere.' },
      { type: 'bullets', title: 'What you can customize', items: [
        { title: 'Custom Domain', body: 'admin.yourcompany.com (we handle the DNS with you).' },
        { title: 'Logo + Favicon', body: 'Replaces the MoveMate logo across admin, portal, and mobile apps.' },
        { title: 'Colour Palette', body: 'Primary + accent colours flow through every UI surface.' },
        { title: 'Email Sender', body: 'Send from info@yourcompany.com using your own SMTP (Gmail, SendGrid, or custom).' },
        { title: 'Mobile Apps', body: 'Your logo and colours in the App Store / Play Store listing and inside the app.' },
        { title: 'Support Contact', body: 'Customers contact YOUR support, not MoveMate.' },
      ]},
      { type: 'tip', tone: 'blue', body: 'White Label is on the £149/mo plan. Setup typically takes 3-5 business days for full DNS + mobile app rebranding.' },
    ],
    related: ['company-settings', 'notification-settings'],
  },

  'estimate-settings': {
    title: 'Estimate Settings',
    category: 'Settings & Configuration',
    iconName: 'Settings',
    readTime: '5 min',
    description: 'Central hub for all pricing, tax, and quote-validity configuration.',
    sections: [
      { type: 'intro', body: 'Estimate Settings holds every variable that goes into calculating a quote — pricing, VAT, minimums, and the rules for how quotes behave.' },
      { type: 'bullets', title: 'All the tabs', items: [
        { title: 'General', body: 'VAT rate, deposit %, quote validity days, default currency.' },
        { title: 'Distance', body: 'Per-mile rate, free miles, return-leg toggle.' },
        { title: 'Labour', body: 'Hourly rates per role, minimum hours, overtime multiplier.' },
        { title: 'Materials', body: 'Boxes, bubble wrap, tape, paper prices.' },
        { title: 'Packing Services', body: 'Per-room, per-hour, or per-volume packing rates.' },
        { title: 'Storage', body: 'Monthly rates and handling fees.' },
      ]},
      { type: 'tip', tone: 'amber', body: 'Any change applies to NEW estimates going forward. Existing estimates keep their original prices unless you click "Recalculate".' },
    ],
    related: ['pricing-setup', 'distance-pricing', 'storage-pricing'],
  },

  'notification-settings': {
    title: 'Notification Settings',
    category: 'Settings & Configuration',
    iconName: 'Bell',
    readTime: '3 min',
    description: 'Control which notifications your customers and team receive, and via which channel (email, SMS, push).',
    sections: [
      { type: 'intro', body: 'Settings → Notifications is the master switch for every automatic message MoveMate sends. For each event, you control the channels: email, SMS, push, or off.' },
      { type: 'bullets', title: 'What you can toggle', items: [
        { title: 'Customer notifications', body: 'Estimate sent, scheduled, in-progress, delivered, invoice, review request.' },
        { title: 'Team notifications', body: 'New job assigned, job rescheduled, customer replied in chat.' },
        { title: 'Admin alerts', body: 'Payment failed, estimate rejected, support message from customer.' },
      ]},
      { type: 'tip', tone: 'green', body: 'SMS notifications require a Twilio integration. Without it, SMS toggles are greyed out but the rest (email, push) work fine.' },
    ],
    related: ['notifications', 'chat'],
  },
};

export default docsContent;
